'use client'

import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faRefresh } from '@fortawesome/pro-regular-svg-icons/faRefresh'
import { useEffect, useRef, useState } from 'react'
import SignaturePad from 'signature_pad'

export const SignatureCanvas = ({
  height = '113',
  name = 'patient-signature',
}: {
  // name of the input field for the form action
  height?: string
  name?: string
  onSignatureChange: () => void
}) => {
  const [canvas, setCanvas] = useState<HTMLCanvasElement>()
  const [pad, setPad] = useState<SignaturePad>()
  const [value, setValue] = useState<string>('') // base64 string
  const [isPlaceholderShown, setShowPlaceholder] = useState<boolean>(true)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (canvas) {
      const signaturePad = new SignaturePad(canvas)
      setPad(signaturePad)

      signaturePad.addEventListener(
        'beginStroke',
        () => {
          setShowPlaceholder(false)
        },
        { once: false },
      )

      signaturePad.addEventListener(
        'afterUpdateStroke',
        () => {
          setValue(signaturePad.toDataURL())
        },
        { once: false },
      )
    }
  }, [canvas])

  useEffect(() => {
    const handleResize = () => {
      const container = containerRef.current
      if (canvas && container) {
        // Save the current drawing as an array of strokes
        const data = pad?.toData()

        // Calculate the scale factor
        const oldWidth = canvas.width
        const oldHeight = canvas.height
        const newWidth = container.clientWidth
        const scaleFactor = newWidth / oldWidth

        // Resize the canvas
        canvas.width = newWidth

        // Set a maximum height constraint
        const maxHeight = 250 // Contact site banner eats up space
        const newHeight = Math.min(oldHeight * scaleFactor, maxHeight)
        canvas.height = newHeight

        // Redraw the saved strokes on the resized canvas with scaling
        if (data) {
          const scaledData = data.map((stroke) => ({
            ...stroke,
            points: stroke.points.map((point) => ({
              ...point,
              x: point.x * scaleFactor,
              y: point.y * (newHeight / oldHeight),
            })),
          }))
          pad?.fromData(scaledData)
        }
      }
    }

    // Set initial width
    handleResize()

    // Add event listener for window resize
    window.addEventListener('resize', handleResize)

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [containerRef.current, canvas, pad])

  return (
    <>
      <div className='relative w-full' ref={containerRef}>
        <div className='flex w-full items-center justify-center'>
          <Button
            className={classMerge(
              buttonClassNames.legacy.accent.lightGrey,
              'absolute right-3 top-3 w-min px-4',
            )}
            onClick={() => {
              if (pad) {
                pad.clear()
                setValue('')
                setShowPlaceholder(true)
              }
            }}
          >
            <Icon icon={faRefresh} />
          </Button>
          <Text
            className={classMerge(
              'pointer-events-none absolute -z-10 p-4',
              isPlaceholderShown ? 'text-neutral400' : 'text-neutral100',
            )}
            styleName='h3'
          >
            Sign here
          </Text>
          <span className='absolute -z-10 mt-16 h-0.5 w-3/4 bg-neutral200' />
          <input
            className='pointer-events-none absolute w-full border border-neutral200 opacity-0'
            name={name}
            onChange={(_e) => {}} // necessary to prevent React warning
            placeholder='signature value'
            required={true}
            type='text'
            value={value}
          />
          <canvas
            className='rounded-xl border border-neutral200'
            height={height}
            id='canvas'
            ref={(e) => {
              if (e === null) return
              setCanvas(e)
            }}
          />
        </div>
      </div>
    </>
  )
}
